"use client"

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  useLayoutEffect,
  type CSSProperties,
} from "react"
import { Globe, ChevronRight } from "lucide-react"
import { useTranslation } from "@/i18n/client"
import { usePathname } from "next/navigation"
import { trackYohoEvent } from "@/lib/analytics/track"
import { supportedLanguages } from "@/i18n/config"
import { localizePathname } from "@/lib/locale-routing"
import "./language-selector.css"

// 添加 Flutter 方法类型声明
declare global {
  interface Window {
    flutterMethod?: (method: string, ...args: any[]) => void
  }
}

const languageEmojis: Record<string, string> = {
  en: "🇺🇸",
  es: "🇪🇸",
  "zh-CN": "🇨🇳",
  "ru-RU": "🇷🇺",
  "ja-JP": "🇯🇵",
  "fr-FR": "🇫🇷",
  ko: "🇰🇷",
  "hi-IN": "🇮🇳",
  "pt-PT": "🇵🇹",
  "zh-TW": "🇹🇼",
    "de-DE": "🇩🇪",
  "ar-SA": "🇸🇦",
  "it-IT": "🇮🇹",
  "id-ID": "🇮🇩",
  "th-TH": "🇹🇭",
  "nl-NL": "🇳🇱",
  "vi-VN": "🇻🇳",
  "ms-MY": "🇲🇾",
  "uk-UA": "🇺🇦",
  "tr-TR": "🇹🇷",
  "pl-PL": "🇵🇱",
  "ro-RO": "🇷🇴",
  "cs-CZ": "🇨🇿",
  "hu-HU": "🇭🇺",
  "el-GR": "🇬🇷",
  "sv-SE": "🇸🇪",
  "nb-NO": "🇳🇴",
  "da-DK": "🇩🇰",
  "fi-FI": "🇫🇮",
  "he-IL": "🇮🇱",
  "bn-BD": "🇧🇩",
  "ur-PK": "🇵🇰",
  "km-KH": "🇰🇭",
  "sr-Cyrl-RS": "🇷🇸",
  "hr-HR": "🇭🇷",
  "sl-SI": "🇸🇮",
  "sk-SK": "🇸🇰",
  "bg-BG": "🇧🇬",
  "et-EE": "🇪🇪",
  "lv-LV": "🇱🇻",
  "lt-LT": "🇱🇹",
  "fa-IR": "🇮🇷",
  "pt-BR": "🇧🇷",
  "zh-HK": "🇭🇰",
  "es-LA": "🌎", // 拉丁美洲用地球emoji
  "ar-EG": "🇪🇬",
  "fr-CA": "🇨🇦",
}


const languageNavLabels: Record<string, string> = {
  en: "EN",
  es: "ES",
  "es-LA": "LA", // Latin American Spanish — using LA so it doesn't collide with ES (Spain)
  "zh-CN": "简中",
  "zh-TW": "台繁",
  "zh-HK": "港繁",
  "ja-JP": "日本",
  ko: "한국",
  "fr-FR": "FR",
  "fr-CA": "CA",
  "ar-SA": "عربي",
  "ar-EG": "EG",
  "vi-VN": "VI",
  "th-TH": "ไทย",
  "id-ID": "ID",
  "ms-MY": "MS",
  "km-KH": "KH",
  "hi-IN": "हिंदी",
  "bn-BD": "বাংলা",
  "ur-PK": "اردو",
  "it-IT": "IT",
  "pt-PT": "PT",
  "pt-BR": "BR",
  "de-DE": "DE",
  "nl-NL": "NL",
  "sv-SE": "SV",
  "da-DK": "DA",
  "fi-FI": "FI",
  "nb-NO": "NO",
  "ru-RU": "RU",
  "pl-PL": "PL",
  "uk-UA": "UK",
  "cs-CZ": "CS",
  "hu-HU": "HU",
  "ro-RO": "RO",
  "bg-BG": "BG",
  "sk-SK": "SK",
  "hr-HR": "HR",
  "sr-Cyrl-RS": "SR",
  "lt-LT": "LT",
  "lv-LV": "LV",
  "et-EE": "ET",
  "sl-SI": "SL",
  "tr-TR": "TR",
  "he-IL": "עברית",
  "fa-IR": "فارسی",
  "el-GR": "EL",
}

function getLanguageNavLabel(locale: string) {
  const label = languageNavLabels[locale]
  if (!label) {
    // Unknown locale: surface the raw code rather than crashing the navbar.
    if (typeof console !== "undefined") {
      console.warn(`[language-selector] no nav label for "${locale}", using raw code`)
    }
    return locale
  }
  return label
}

// Mapping for short codes to specific locales for storage
const shortToLongLocaleMap: Record<string, string> = {
  en: "en-US",
  es: "es-ES",
  ko: "kr-KR",
    "zh-CN": "zh-CN",
  "ru-RU": "ru-RU",
  "ja-JP": "ja-JP",
  "fr-FR": "fr-FR",
  "hi-IN": "hi-IN",
  "pt-PT": "pt-PT",
  "zh-TW": "zh-TW",
  "de-DE": "de-DE",
  "ar-SA": "ar-SA",
  "it-IT": "it-IT",
  "id-ID": "id-ID",
  "th-TH": "th-TH",
  "nl-NL": "nl-NL",
  "vi-VN": "vi-VN",
  "ms-MY": "ms-MY",
  "uk-UA": "uk-UA",
  "tr-TR": "tr-TR",
  "pl-PL": "pl-PL",
  "ro-RO": "ro-RO",
  "cs-CZ": "cs-CZ",
  "hu-HU": "hu-HU",
  "el-GR": "el-GR",
  "sv-SE": "sv-SE",
  "nb-NO": "no-NO",
  "da-DK": "da-DK",
  "fi-FI": "fi-FI",
  "he-IL": "he-IL",
  "bn-BD": "bn-BD",
  "ur-PK": "ur-PK",
  "km-KH": "km-KH",
  "sr-Cyrl-RS": "sr-RS",
  "hr-HR": "hr-HR",
  "sl-SI": "sl-SI",
  "sk-SK": "sk-SK",
  "bg-BG": "bg-BG",
  "et-EE": "et-EE",
  "lv-LV": "lv-LV",
  "lt-LT": "lt-LT",
  "fa-IR": "fa-IR",
  "pt-BR": "pt-BR",
  "zh-HK": "zh-HK",
  "es-LA": "es-LA",
  "ar-EG": "ar-EG",
  "fr-CA": "fr-CA",
}


const languageSelectorText: Record<string, {
  selectLabel: string
  searchPlaceholder: string
  searchLabel: string
  noMatches: string
  closeLabel: string
  dialogTitle: string
}> = {
  en: {
    selectLabel: "Select language",
    searchPlaceholder: "Search language or region",
    searchLabel: "Search languages",
    noMatches: "No matching languages",
    closeLabel: "Close language selector",
    dialogTitle: "Select language",
  },
  es: {
    selectLabel: "Seleccionar idioma",
    searchPlaceholder: "Buscar idioma o región",
    searchLabel: "Buscar idiomas",
    noMatches: "Sin idiomas coincidentes",
    closeLabel: "Cerrar selector de idioma",
    dialogTitle: "Seleccionar idioma",
  },
  "zh-CN": {
    selectLabel: "选择语言",
    searchPlaceholder: "搜索语言或地区",
    searchLabel: "搜索语言",
    noMatches: "没有匹配的语言",
    closeLabel: "关闭语言选择器",
    dialogTitle: "选择语言",
  },
  "zh-TW": {
    selectLabel: "選擇語言",
    searchPlaceholder: "搜尋語言或地區",
    searchLabel: "搜尋語言",
    noMatches: "沒有符合的語言",
    closeLabel: "關閉語言選擇器",
    dialogTitle: "選擇語言",
  },
  "zh-HK": {
    selectLabel: "揀語言",
    searchPlaceholder: "搜尋語言或地區",
    searchLabel: "搜尋語言",
    noMatches: "搵唔到符合嘅語言",
    closeLabel: "關閉語言選擇器",
    dialogTitle: "揀語言",
  },
  "ja-JP": {
    selectLabel: "言語を選択",
    searchPlaceholder: "言語または地域を検索",
    searchLabel: "言語を検索",
    noMatches: "一致する言語がありません",
    closeLabel: "言語選択を閉じる",
    dialogTitle: "言語を選択",
  },
  ko: {
    selectLabel: "언어 선택",
    searchPlaceholder: "언어 또는 지역 검색",
    searchLabel: "언어 검색",
    noMatches: "일치하는 언어가 없습니다",
    closeLabel: "언어 선택 닫기",
    dialogTitle: "언어 선택",
  },
  "fr-FR": {
    selectLabel: "Sélectionner la langue",
    searchPlaceholder: "Rechercher une langue ou une région",
    searchLabel: "Rechercher des langues",
    noMatches: "Aucune langue correspondante",
    closeLabel: "Fermer le sélecteur de langue",
    dialogTitle: "Sélectionner la langue",
  },
  "ar-SA": {
    selectLabel: "اختيار اللغة",
    searchPlaceholder: "ابحث عن لغة أو منطقة",
    searchLabel: "البحث في اللغات",
    noMatches: "لا توجد لغات مطابقة",
    closeLabel: "إغلاق محدد اللغة",
    dialogTitle: "اختيار اللغة",
  },
  "vi-VN": {
    selectLabel: "Chọn ngôn ngữ",
    searchPlaceholder: "Tìm ngôn ngữ hoặc khu vực",
    searchLabel: "Tìm kiếm ngôn ngữ",
    noMatches: "Không có ngôn ngữ phù hợp",
    closeLabel: "Đóng bộ chọn ngôn ngữ",
    dialogTitle: "Chọn ngôn ngữ",
  },
  "th-TH": {
    selectLabel: "เลือกภาษา",
    searchPlaceholder: "ค้นหาภาษาหรือภูมิภาค",
    searchLabel: "ค้นหาภาษา",
    noMatches: "ไม่พบภาษาที่ตรงกัน",
    closeLabel: "ปิดตัวเลือกภาษา",
    dialogTitle: "เลือกภาษา",
  },
  "id-ID": {
    selectLabel: "Pilih bahasa",
    searchPlaceholder: "Cari bahasa atau wilayah",
    searchLabel: "Cari bahasa",
    noMatches: "Tidak ada bahasa yang cocok",
    closeLabel: "Tutup pemilih bahasa",
    dialogTitle: "Pilih bahasa",
  },
  "ms-MY": {
    selectLabel: "Pilih bahasa",
    searchPlaceholder: "Cari bahasa atau rantau",
    searchLabel: "Cari bahasa",
    noMatches: "Tiada bahasa yang sepadan",
    closeLabel: "Tutup pemilih bahasa",
    dialogTitle: "Pilih bahasa",
  },
  "hi-IN": {
    selectLabel: "भाषा चुनें",
    searchPlaceholder: "भाषा या क्षेत्र खोजें",
    searchLabel: "भाषाएँ खोजें",
    noMatches: "कोई मिलती-जुलती भाषा नहीं",
    closeLabel: "भाषा चयन बंद करें",
    dialogTitle: "भाषा चुनें",
  },
  "bn-BD": {
    selectLabel: "ভাষা নির্বাচন করুন",
    searchPlaceholder: "ভাষা বা অঞ্চল খুঁজুন",
    searchLabel: "ভাষা খুঁজুন",
    noMatches: "মিল থাকা কোনো ভাষা নেই",
    closeLabel: "ভাষা নির্বাচক বন্ধ করুন",
    dialogTitle: "ভাষা নির্বাচন করুন",
  },
  "ur-PK": {
    selectLabel: "زبان منتخب کریں",
    searchPlaceholder: "زبان یا علاقہ تلاش کریں",
    searchLabel: "زبانیں تلاش کریں",
    noMatches: "کوئی ملتی جلتی زبان نہیں",
    closeLabel: "زبان منتخب کرنے والا بند کریں",
    dialogTitle: "زبان منتخب کریں",
  },
  "it-IT": {
    selectLabel: "Seleziona lingua",
    searchPlaceholder: "Cerca lingua o regione",
    searchLabel: "Cerca lingue",
    noMatches: "Nessuna lingua corrispondente",
    closeLabel: "Chiudi selettore lingua",
    dialogTitle: "Seleziona lingua",
  },
  "pt-PT": {
    selectLabel: "Selecionar idioma",
    searchPlaceholder: "Pesquisar idioma ou região",
    searchLabel: "Pesquisar idiomas",
    noMatches: "Nenhum idioma correspondente",
    closeLabel: "Fechar seletor de idioma",
    dialogTitle: "Selecionar idioma",
  },
  "de-DE": {
    selectLabel: "Sprache auswählen",
    searchPlaceholder: "Sprache oder Region suchen",
    searchLabel: "Sprachen suchen",
    noMatches: "Keine passenden Sprachen",
    closeLabel: "Sprachauswahl schließen",
    dialogTitle: "Sprache auswählen",
  },
  "nl-NL": {
    selectLabel: "Taal selecteren",
    searchPlaceholder: "Zoek taal of regio",
    searchLabel: "Talen zoeken",
    noMatches: "Geen overeenkomende talen",
    closeLabel: "Taalkiezer sluiten",
    dialogTitle: "Taal selecteren",
  },
  "sv-SE": {
    selectLabel: "Välj språk",
    searchPlaceholder: "Sök språk eller region",
    searchLabel: "Sök språk",
    noMatches: "Inga matchande språk",
    closeLabel: "Stäng språkväljaren",
    dialogTitle: "Välj språk",
  },
  "da-DK": {
    selectLabel: "Vælg sprog",
    searchPlaceholder: "Søg efter sprog eller region",
    searchLabel: "Søg i sprog",
    noMatches: "Ingen matchende sprog",
    closeLabel: "Luk sprogvælger",
    dialogTitle: "Vælg sprog",
  },
  "fi-FI": {
    selectLabel: "Valitse kieli",
    searchPlaceholder: "Etsi kieltä tai aluetta",
    searchLabel: "Etsi kieliä",
    noMatches: "Ei vastaavia kieliä",
    closeLabel: "Sulje kielivalitsin",
    dialogTitle: "Valitse kieli",
  },
  "nb-NO": {
    selectLabel: "Velg språk",
    searchPlaceholder: "Søk etter språk eller region",
    searchLabel: "Søk i språk",
    noMatches: "Ingen samsvarende språk",
    closeLabel: "Lukk språkvelger",
    dialogTitle: "Velg språk",
  },
  "ru-RU": {
    selectLabel: "Выбрать язык",
    searchPlaceholder: "Поиск языка или региона",
    searchLabel: "Поиск языков",
    noMatches: "Нет подходящих языков",
    closeLabel: "Закрыть выбор языка",
    dialogTitle: "Выбрать язык",
  },
  "pl-PL": {
    selectLabel: "Wybierz język",
    searchPlaceholder: "Szukaj języka lub regionu",
    searchLabel: "Szukaj języków",
    noMatches: "Brak pasujących języków",
    closeLabel: "Zamknij wybór języka",
    dialogTitle: "Wybierz język",
  },
  "uk-UA": {
    selectLabel: "Вибрати мову",
    searchPlaceholder: "Пошук мови або регіону",
    searchLabel: "Пошук мов",
    noMatches: "Немає відповідних мов",
    closeLabel: "Закрити вибір мови",
    dialogTitle: "Вибрати мову",
  },
  "cs-CZ": {
    selectLabel: "Vybrat jazyk",
    searchPlaceholder: "Hledat jazyk nebo oblast",
    searchLabel: "Hledat jazyky",
    noMatches: "Žádné odpovídající jazyky",
    closeLabel: "Zavřít výběr jazyka",
    dialogTitle: "Vybrat jazyk",
  },
  "hu-HU": {
    selectLabel: "Nyelv kiválasztása",
    searchPlaceholder: "Nyelv vagy régió keresése",
    searchLabel: "Nyelvek keresése",
    noMatches: "Nincs találat",
    closeLabel: "Nyelvválasztó bezárása",
    dialogTitle: "Nyelv kiválasztása",
  },
  "ro-RO": {
    selectLabel: "Selectează limba",
    searchPlaceholder: "Caută limbă sau regiune",
    searchLabel: "Caută limbi",
    noMatches: "Nu există limbi potrivite",
    closeLabel: "Închide selectorul de limbă",
    dialogTitle: "Selectează limba",
  },
  "bg-BG": {
    selectLabel: "Избор на език",
    searchPlaceholder: "Търсене на език или регион",
    searchLabel: "Търсене на езици",
    noMatches: "Няма съвпадащи езици",
    closeLabel: "Затваряне на избора на език",
    dialogTitle: "Избор на език",
  },
  "sk-SK": {
    selectLabel: "Vybrať jazyk",
    searchPlaceholder: "Hľadať jazyk alebo región",
    searchLabel: "Hľadať jazyky",
    noMatches: "Žiadne zodpovedajúce jazyky",
    closeLabel: "Zavrieť výber jazyka",
    dialogTitle: "Vybrať jazyk",
  },
  "sr-Cyrl-RS": {
    selectLabel: "Изаберите језик",
    searchPlaceholder: "Претражите језик или регион",
    searchLabel: "Претражи језике",
    noMatches: "Нема одговарајућих језика",
    closeLabel: "Затвори избор језика",
    dialogTitle: "Изаберите језик",
  },
  "lt-LT": {
    selectLabel: "Pasirinkti kalbą",
    searchPlaceholder: "Ieškoti kalbos arba regiono",
    searchLabel: "Ieškoti kalbų",
    noMatches: "Nėra atitinkančių kalbų",
    closeLabel: "Uždaryti kalbos pasirinkimą",
    dialogTitle: "Pasirinkti kalbą",
  },
  "et-EE": {
    selectLabel: "Vali keel",
    searchPlaceholder: "Otsi keelt või piirkonda",
    searchLabel: "Otsi keeli",
    noMatches: "Sobivaid keeli pole",
    closeLabel: "Sulge keelevalik",
    dialogTitle: "Vali keel",
  },
  "sl-SI": {
    selectLabel: "Izberi jezik",
    searchPlaceholder: "Poišči jezik ali regijo",
    searchLabel: "Išči jezike",
    noMatches: "Ni ujemajočih se jezikov",
    closeLabel: "Zapri izbirnik jezika",
    dialogTitle: "Izberi jezik",
  },
  "tr-TR": {
    selectLabel: "Dil seç",
    searchPlaceholder: "Dil veya bölge ara",
    searchLabel: "Dilleri ara",
    noMatches: "Eşleşen dil yok",
    closeLabel: "Dil seçiciyi kapat",
    dialogTitle: "Dil seç",
  },
  "he-IL": {
    selectLabel: "בחר שפה",
    searchPlaceholder: "חפש שפה או אזור",
    searchLabel: "חפש שפות",
    noMatches: "אין שפות תואמות",
    closeLabel: "סגור את בוחר השפה",
    dialogTitle: "בחר שפה",
  },
  "fa-IR": {
    selectLabel: "انتخاب زبان",
    searchPlaceholder: "جستجوی زبان یا منطقه",
    searchLabel: "جستجوی زبان‌ها",
    noMatches: "زبان منطبقی پیدا نشد",
    closeLabel: "بستن انتخابگر زبان",
    dialogTitle: "انتخاب زبان",
  },
  "el-GR": {
    selectLabel: "Επιλογή γλώσσας",
    searchPlaceholder: "Αναζήτηση γλώσσας ή περιοχής",
    searchLabel: "Αναζήτηση γλωσσών",
    noMatches: "Δεν βρέθηκαν αντίστοιχες γλώσσες",
    closeLabel: "Κλείσιμο επιλογέα γλώσσας",
    dialogTitle: "Επιλογή γλώσσας",
  },
}

function getLanguageSelectorText(lng: string) {
  const text = languageSelectorText[lng]
  if (!text) {
    if (typeof console !== "undefined") {
      console.warn(`[language-selector] no copy for "${lng}", falling back to en`)
    }
    return languageSelectorText.en
  }
  return text
}

interface LanguageSelectorProps {
  lng: string
  variant?: "compact" | "row"
}

// Helper function to close mobile menu if open
const closeMobileMenuIfOpen = () => {
  const mobileMenu = document.getElementById("mobile-menu")
  // Check if menu exists and is open
  if (mobileMenu && mobileMenu.classList.contains("open")) {
    mobileMenu.classList.remove("open") // Close the menu
    document.body.style.overflow = "" // Restore body scroll
    const menuToggleIcon = document.querySelector("#menu-toggle span")
    if (menuToggleIcon) {
      menuToggleIcon.textContent = "≡" // Reset toggle icon
    }
    document.dispatchEvent(new CustomEvent("yoho:mobile-menu-close"))
  }
}


export const LanguageSelector = ({ lng, variant = "compact" }: LanguageSelectorProps) => {
  const isRow = variant === "row"
  const { t } = useTranslation(lng, "translation")
  const [isOpen, setIsOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [displayLocale, setDisplayLocale] = useState(lng)
  const pathname = usePathname()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const triggerRef = useRef<HTMLButtonElement | null>(null)
  const listRef = useRef<HTMLDivElement | null>(null)
  const [dialogStyle, setDialogStyle] = useState<CSSProperties>({})

  useEffect(() => {
    setDisplayLocale(lng)
  }, [lng])

  const updateDialogPosition = useCallback(() => {
    if (typeof window === "undefined" || !triggerRef.current) return

    const triggerRect = triggerRef.current.getBoundingClientRect()
    const viewportWidth = window.innerWidth
    const viewportHeight = window.innerHeight
    const margin = viewportWidth <= 480 ? 12 : 16
    const panelWidth = Math.min(420, viewportWidth - margin * 2)
    const preferredLeft = triggerRect.right - panelWidth
    const maxLeft = Math.max(margin, viewportWidth - panelWidth - margin)
    const left = Math.min(Math.max(preferredLeft, margin), maxLeft)
    const top = Math.min(
      triggerRect.bottom + 10,
      Math.max(12, viewportHeight - 94)
    )
    const arrowLeft = Math.min(
      Math.max(triggerRect.left + triggerRect.width / 2 - left, 28),
      panelWidth - 28
    )

    setDialogStyle({
      top: `${Math.round(top)}px`,
      left: `${Math.round(left)}px`,
      width: `${Math.round(panelWidth)}px`,
      "--dialog-arrow-left": `${Math.round(arrowLeft)}px`,
    } as CSSProperties & Record<"--dialog-arrow-left", string>)
  }, [])

  useLayoutEffect(() => {
    if (!isOpen) return
    updateDialogPosition()
  }, [isOpen, updateDialogPosition])

  useEffect(() => {
    if (!isOpen) return
    const handleReposition = () => updateDialogPosition()
    window.addEventListener("resize", handleReposition)
    window.addEventListener("scroll", handleReposition, true)
    return () => {
      window.removeEventListener("resize", handleReposition)
      window.removeEventListener("scroll", handleReposition, true)
    }
  }, [isOpen, updateDialogPosition])

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    const handlePointer = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsOpen(false)
    }
    document.addEventListener("pointerdown", handlePointer)
    document.addEventListener("keydown", handleKey)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("pointerdown", handlePointer)
      document.removeEventListener("keydown", handleKey)
    }
  }, [isOpen])

  useEffect(() => {
    if (!isOpen || !listRef.current) return
    const node = listRef.current.querySelector<HTMLElement>(
      `[data-locale="${displayLocale}"]`
    )
    if (!node) return
    const list = listRef.current
    const itemTop = node.offsetTop
    const itemBottom = itemTop + node.offsetHeight
    const visibleTop = list.scrollTop
    const visibleBottom = visibleTop + list.clientHeight

    if (itemTop < visibleTop) {
      list.scrollTop = Math.max(0, itemTop - 8)
    } else if (itemBottom > visibleBottom) {
      list.scrollTop = itemBottom - list.clientHeight + 8
    }
  }, [isOpen, displayLocale])

  useEffect(() => {
    if (!isOpen) setSearchQuery("")
  }, [isOpen])

  // 检查是否在 Flutter 环境中
  const isFlutterEnvironment =
    typeof window !== "undefined" && window.flutterMethod

  const displayedLanguageDetails = supportedLanguages.find(
    (lang) => lang.locale === displayLocale
  )

  const handleLanguageChange = (locale: string) => {
    // Determine the value to store and potentially pass to Flutter
    const localeToStore = shortToLongLocaleMap[locale] || locale

    // Write the potentially mapped locale to localStorage and set cookie
    try {
      localStorage.setItem("selected-language", localeToStore)
      // 设置 cookie，有效期 365 天
      document.cookie = `selected-language=${localeToStore}; path=/; max-age=${365 * 24 * 60 * 60}`
      console.log(
        `Stored language in localStorage and cookie: ${localeToStore}`
      ) // Optional logging
    } catch (error) {
      console.error("Error writing language to localStorage or cookie:", error)
    }

    trackYohoEvent("language_change", {
      previous_locale: displayLocale,
      next_locale: localeToStore,
    })

    // Update the displayed locale immediately
    setDisplayLocale(locale)

    if (isFlutterEnvironment) {
      // 在 Flutter 环境中，调用 Flutter 方法，使用映射后的 locale
      window.flutterMethod?.("switchLang", localeToStore)
      console.log(`Called switchLang with mapped locale: ${localeToStore}`)
      // 刷新当前页面以确保 React Header 等静态区域能够使用新的语言重新渲染
      // (Flutter Web 会自动重新初始化，但保持语言状态)
      if (typeof window !== "undefined") {
        // 轻量刷新，保持原 URL
        window.location.reload()
      }
    } else {
      const nextPath = localizePathname(pathname, locale)
      const search = typeof window !== "undefined" ? window.location.search : ""
      const hash = typeof window !== "undefined" ? window.location.hash : ""
      window.location.assign(`${nextPath}${search}${hash}`)
    }
    setIsOpen(false)

    // Close mobile menu if it was open
    closeMobileMenuIfOpen()
  }

  const currentLanguageName = displayedLanguageDetails?.name || displayLocale
  const languageText = getLanguageSelectorText(lng)
  const selectLanguageLabel = languageText.selectLabel
  const languageSearchPlaceholder = languageText.searchPlaceholder
  const languageSearchLabel = languageText.searchLabel
  const languageNoMatches = languageText.noMatches

  // Header chip uses explicit short labels for every enabled locale, so
  // Chinese variants are not special-cased and East Asian labels can stay local.
  const triggerCode = getLanguageNavLabel(displayLocale)

  return (
    <div
      className={`language-selector${isRow ? " language-selector-row" : ""}`}
      ref={containerRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className={`language-trigger${isRow ? " language-trigger-row" : ""}`}
        aria-label={`${selectLanguageLabel}，${currentLanguageName}`}
        title={currentLanguageName}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
      >
        {displayedLanguageDetails && (
          <>
            <Globe
              className="language-nav-icon"
              aria-hidden="true"
              strokeWidth={2.2}
            />
            {isRow ? (
              <>
                <span className="language-name-row">{currentLanguageName}</span>
                <ChevronRight
                  className="language-chevron"
                  aria-hidden="true"
                  strokeWidth={2}
                />
              </>
            ) : (
              <span className="language-name-nav">{triggerCode}</span>
            )}
          </>
        )}
      </button>

      {isOpen && (
        <div
          className="language-dialog"
          style={dialogStyle}
          onPointerDown={(event) => {
            if (event.target === event.currentTarget) setIsOpen(false)
          }}
        >
          <div
            className="language-dialog-content"
            role="dialog"
            aria-modal="true"
            aria-labelledby="language-dialog-title"
          >
            <div className="language-dialog-header">
              <h3 id="language-dialog-title">{languageText.dialogTitle}</h3>
              <button
                type="button"
                className="close-button"
                aria-label={languageText.closeLabel}
                onClick={() => setIsOpen(false)}
              >
                ×
              </button>
            </div>

            <div className="search-container">
              <input
                type="search"
                className="search-input"
                placeholder={languageSearchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                aria-label={languageSearchLabel}
              />
            </div>

            <div className="language-list" ref={listRef}>
              {(() => {
                const renderItem = (
                  lang: (typeof supportedLanguages)[number]
                ) => (
                  <button
                    type="button"
                    key={lang.locale}
                    data-locale={lang.locale}
                    className={`language-item ${lang.locale === displayLocale ? "selected" : ""}`}
                    aria-pressed={lang.locale === displayLocale}
                    onClick={() => handleLanguageChange(lang.locale)}
                  >
                    <span className="language-emoji noto-color-emoji-regular">
                      {languageEmojis[lang.locale]}
                    </span>
                    <span className="language-name">{lang.name}</span>
                  </button>
                )

                const q = searchQuery.trim().toLowerCase()
                const collator = new Intl.Collator(displayLocale, {
                  sensitivity: "base",
                  numeric: true,
                })
                const sortedLanguages = [...supportedLanguages].sort(
                  (left, right) =>
                    collator.compare(left.name, right.name) ||
                    collator.compare(left.locale, right.locale)
                )
                const matches = q
                  ? sortedLanguages.filter(
                      (l) =>
                        l.name.toLowerCase().includes(q) ||
                        l.locale.toLowerCase().includes(q)
                    )
                  : sortedLanguages

                if (matches.length === 0) {
                  return (
                    <div className="language-empty">
                      {languageNoMatches}
                    </div>
                  )
                }

                return <>{matches.map(renderItem)}</>
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
